/// <amd-module name="Wit/WorkItemBuildLinks/Components/BuildPickerDialog" />
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";

import { getCoreClient } from "Tfs/Platform/RestClient/Core";
import * as Core from "Tfs/Platform/Contracts/Core";
import { useObservable } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { DatePicker } from "VSSUI/DatePicker";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { PanelFooter } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";

import { Build, BuildResult } from "Build/Client/Contracts/Build";
import { Observer } from "VSSUI/Observer";

import { BuildsPreviewTable } from "./BuildsPreviewTable";
import { getBuildClient, IBuildRestClient } from "Build/Client/RestClient/Build";
import { ListSelection } from "VSSUI/List";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { usePageContext } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";

import * as Resources from "Wit/WorkItemBuildLinks/Resources";
import { getFriendlyBuildResult } from "../BuildLinkUtils";

interface IBuildPickerDialogProps {
    onDismiss: () => void;
    onBuildChosen: (build: Build | undefined) => void;
}

const maxBuildsToPreview = 50;

const resultOptions = [BuildResult.Succeeded, BuildResult.PartiallySucceeded, BuildResult.Failed, BuildResult.Canceled].map(result => ({
    id: "" + result,
    data: result,
    text: getFriendlyBuildResult(result)
}));

export function BuildPickerDialog({ onDismiss, onBuildChosen }: IBuildPickerDialogProps) {
    const [project, setProject] = useObservable<string | undefined>(undefined);
    const [projectOptions, setProjectOptions] = useObservable<IListBoxItem<{}>[] | undefined>(undefined);
    const [buildNumber, setBuidNumber] = useObservable<string | undefined>(undefined);
    const [pipeline, setPipeline] = useObservable<number | undefined>(undefined);
    const [pipelineOptions, setPipelineOptions] = useObservable<IListBoxItem<{}>[] | undefined>([]);
    const [result, setResult] = useObservable<BuildResult | undefined>(undefined);
    const [fromDate, setFromDate] = useObservable<Date | undefined>(undefined);
    const [toDate, setToDate] = useObservable<Date | undefined>(undefined);

    const [hasSearched, setHasSearched] = useObservable(false);
    const [loadingBuilds, setLoadingBuilds] = useObservable(false);
    const [matchingBuilds, setMatchingBuilds] = useObservable<Build[]>([]);
    const selection = useRef(new ListSelection({ selectOnFocus: false, multiSelect: false }));

    const pageContext = usePageContext();
    const coreClient = useRef(getCoreClient(pageContext));
    const buildClient = useRef(getBuildClient(pageContext));

    // Initialize the project options on load
    useEffect(() => {
        coreClient.current.getProjects(Core.ProjectState.WellFormed, 1000).then(projectRefs => {
            const items = projectRefs.map(ref => ({ id: ref.id, text: ref.name })).sort((a, b) => localeIgnoreCaseComparer(a.text, b.text));
            setProjectOptions(items);
        });
    }, []);

    // Initialize the pipeline options when the project changes
    useEffect(() => {
        const fillPipelineOptions = async (project: string) => {
            setPipelineOptions(undefined);
            setPipeline(undefined);
            const defs = await buildClient.current.getDefinitions(project);
            setPipelineOptions(defs.map(def => ({ id: "" + def.id, text: def.name })));
        };
        project.subscribe(fillPipelineOptions);
        return () => project.unsubscribe(fillPipelineOptions);
    }, []);

    // Enable or disable the confirm button when the selected builds change
    const [confirmDisabled, setConfirmDisabled] = useObservable(true);
    useEffect(() => {
        const onSelectionChange = (s: ISelectionRange[]) => setConfirmDisabled(s.length === 0);
        // TODO:replat this doesn't give the right result when items are deselected
        selection.current.subscribe(onSelectionChange);
        return () => selection.current.unsubscribe(onSelectionChange);
    }, []);

    const findBuilds = useCallback(() => {
        if (!project.value) {
            return;
        }

        setHasSearched(true);
        setLoadingBuilds(true);
        getMatchingBuilds(buildClient.current, project.value, buildNumber.value, pipeline.value, result.value, fromDate.value, toDate.value).then(
            builds => {
                setMatchingBuilds(builds);
                setLoadingBuilds(false);
            }
        );
    }, []);

    const commitSelectedBuilds = useCallback(() => {
        const selectedBuild = matchingBuilds.value.find((_b, index) => selection.current.selected(index));
        onBuildChosen(selectedBuild);
    }, []);

    return (
        <Dialog titleProps={{ text: Resources.FindBuildsDialogTitle }} onDismiss={onDismiss} contentSize={ContentSize.Large}>
            <div className="build-picker-form-grid flex-row flex-wrap">
                <FormItem className="build-picker-form-item" label={Resources.ProjectLabel}>
                    <Observer items={projectOptions}>
                        {/* TODO:replat the current project should be selected by default */}
                        {({ items }: { items: IListBoxItem<{}>[] | undefined }) => (
                            <Dropdown loading={!items} disabled={!items} items={items || []} onSelect={(e, item) => setProject(item.text)} />
                        )}
                    </Observer>
                </FormItem>
                <FormItem className="build-picker-form-item" label={Resources.BuildNumberLabel}>
                    <TextField value={buildNumber} onChange={(e, val) => setBuidNumber(val)} />
                </FormItem>
                <FormItem className="build-picker-form-item" label={Resources.PipelineLabel}>
                    <Observer items={pipelineOptions}>
                        {({ items }: { items: IListBoxItem<{}>[] | undefined }) => (
                            <Dropdown
                                loading={!items}
                                disabled={!items || items.length === 0}
                                items={items || []}
                                onSelect={(e, item) => setPipeline(parseInt(item.id))}
                            />
                        )}
                    </Observer>
                </FormItem>
                <FormItem className="build-picker-form-item" label={Resources.BuildResultLabel}>
                    <Dropdown items={resultOptions} onSelect={(e, item) => setResult(item.data)} />
                </FormItem>
                <FormItem className="build-picker-form-item" label={Resources.BuildMinDateLabel}>
                    <DatePicker value={fromDate} onChange={setFromDate} />
                </FormItem>
                <FormItem className="build-picker-form-item" label={Resources.BuildMaxDateLabel}>
                    <DatePicker value={toDate} onChange={setToDate} />
                </FormItem>
            </div>

            <div className="flex-row flex-end margin-top-8">
                <Observer project={project}>
                    {({ project }: { project: string | undefined }) => (
                        <Button text={Resources.FindBuildsButtonLabel} disabled={!project} onClick={findBuilds} />
                    )}
                </Observer>
            </div>

            <div className="builds-preview-table-container flex-column">
                <Observer builds={matchingBuilds} loading={loadingBuilds} hasSearched={hasSearched}>
                    {({ builds, loading, hasSearched }: { builds: Build[]; loading: boolean; hasSearched: boolean }) => {
                        if (!hasSearched) {
                            return null;
                        }

                        if (loading) {
                            return <Spinner />;
                        }

                        if (!builds.length) {
                            return <div className="no-matching-builds-message margin-top-16">{Resources.NoMatchingBuildsError}</div>;
                        }

                        return <BuildsPreviewTable builds={builds} selection={selection.current} />;
                    }}
                </Observer>
            </div>

            <Observer confirmDisabled={confirmDisabled}>
                {({ confirmDisabled }: { confirmDisabled: boolean }) => (
                    <PanelFooter
                        buttonProps={[
                            {
                                primary: true,
                                text: Resources.ConfirmBuildSelectionButtonLabel,
                                onClick: commitSelectedBuilds,
                                disabled: confirmDisabled
                            },
                            { text: Resources.CancelBuildSelectionButtonLabel, onClick: onDismiss }
                        ]}
                    />
                )}
            </Observer>
        </Dialog>
    );
}

async function getMatchingBuilds(
    buildClient: IBuildRestClient,
    project: string,
    buildNumber: string | undefined,
    pipeline: number | undefined,
    result: BuildResult | undefined,
    fromDate: Date | undefined,
    toDate: Date | undefined
): Promise<Build[]> {
    return await buildClient.getBuilds(
        project,
        pipeline ? [pipeline] : undefined,
        undefined,
        buildNumber,
        fromDate,
        toDate,
        undefined,
        undefined,
        undefined,
        result,
        undefined,
        undefined,
        maxBuildsToPreview
    );
}
